import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDayLabel } from "@/lib/time";
import { IndianRupee, Users, TrendingUp, Sparkles, Target, Star, CheckCircle2, ChevronRight, X, ShieldAlert, ShieldCheck, Clock, ShieldX } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Interactive Walkthrough ────────────────────────────────────────────────────

const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Your Partner Portal",
    body: "Track referrals, commissions, and AI-powered insights — all in one place. Let's take a quick tour.",
    target: null,
    position: "center",
  },
  {
    id: "stats",
    title: "Your Performance at a Glance",
    body: "These 3 cards show your total referrals, pending commissions, and lifetime earnings. Updated in real-time.",
    target: "partner-kpi-cards",
    position: "bottom",
  },
  {
    id: "ai-brief",
    title: "AI Income Coach",
    body: "Every day, your AI Coach analyzes your pipeline and surfaces the most important actions to grow your income.",
    target: "partner-ai-brief",
    position: "top",
  },
  {
    id: "commissions",
    title: "Earnings Tracking",
    body: "See every earning — pending, approved, and paid. Click 'My Earnings' in the sidebar for the full list.",
    target: "partner-recent-commissions",
    position: "top",
  },
  {
    id: "profile",
    title: "Profile & Bank Details",
    body: "Keep your KYC and bank details up to date to ensure fast commission payouts. Go to 'Profile & Bank' anytime.",
    target: null,
    position: "center",
  },
];

function TourOverlay({ step, total, onNext, onSkip }) {
  const isCenter = !step.target || step.position === "center";
  const s = TOUR_STEPS.indexOf(step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="partner-tour-overlay">
      <div className="absolute inset-0 bg-black/70" onClick={onSkip} />
      <div className="relative z-10 bg-card border border-primary/40 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} className={`h-1 rounded-full transition-all ${i === s ? "w-6 bg-primary" : "w-2 bg-muted-foreground/30"}`} />
            ))}
          </div>
          <button onClick={onSkip} className="text-gray-500 hover:text-foreground transition-colors" data-testid="partner-tour-skip">
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="font-serif text-lg text-foreground mb-2">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">{step.body}</p>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">{s + 1} of {total}</span>
          <button
            onClick={onNext}
            data-testid="partner-tour-next"
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-colors"
          >
            {s === total - 1 ? "Get Started" : "Next"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── KYC Status Card (shown when not verified) ────────────────────────────────

const KYC_CARD_CONFIGS = {
  admin_review: { Icon: Clock,       bg: "bg-amber-500/5 border-amber-500/20", icon: "text-amber-500", text: "Your KYC is under manual review by our team. Usually takes 1–2 business days.", label: "Under Review" },
  rejected:     { Icon: ShieldX,     bg: "bg-red-500/5   border-red-500/20",   icon: "text-red-500",   text: "Your KYC was rejected. Review the reason and update your details.",           label: "Rejected" },
  query_raised: { Icon: ShieldAlert, bg: "bg-blue-500/5  border-blue-500/20",  icon: "text-blue-500",  text: "Our team has a query on your KYC application. Please contact support.",        label: "Query Raised" },
  pending:      { Icon: ShieldAlert, bg: "bg-primary/5   border-primary/20",   icon: "text-primary",   text: "Complete your PAN and Bank verification to activate your account.",            label: "Pending" },
};

function KycStatusCard({ kycStatus }) {
  const cfg = KYC_CARD_CONFIGS[kycStatus] || KYC_CARD_CONFIGS.pending;
  const { Icon, bg, icon, text, label } = cfg;
  return (
    <Link to="/partner/kyc-status"
      className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-sm ${bg}`}
      data-testid="dashboard-kyc-status-card">
      <div className="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center shrink-0">
        <Icon className={`w-4 h-4 ${icon}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[11px] font-semibold uppercase tracking-[0.1em] ${icon}`}>{label}</span>
          <span className="text-[10px] text-muted-foreground">KYC Verification</span>
        </div>
        <p className="text-[12px] text-muted-foreground leading-relaxed truncate">{text}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </Link>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:border-primary/30 transition-all" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl font-light text-foreground">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
}

// ── AI Brief ──────────────────────────────────────────────────────────────────

const ICON_MAP = {
  rupee: IndianRupee,
  star: Star,
  target: Target,
  check: CheckCircle2,
};

const PRIORITY_STYLES = {
  high: "border-l-2 border-primary bg-primary/5",
  medium: "border-l-2 border-blue-500/50 bg-blue-500/5",
  low: "border-l-2 border-emerald-500/50 bg-emerald-500/5",
};

function AIBriefCard({ brief, onToggleAI, toggling }) {
  if (!brief) return (
    <div className="bg-card border border-border rounded-2xl p-5 animate-pulse h-48" />
  );

  const isAI = brief.ai_coach_enabled;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="partner-ai-brief">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
        <span className="text-[11px] uppercase tracking-[0.15em] text-primary font-medium">AI Income Coach</span>
        <span className="ml-auto text-[11px] text-gray-600">{brief.date}</span>
        {/* AI Toggle */}
        <button
          onClick={onToggleAI}
          disabled={toggling}
          data-testid="ai-coach-toggle"
          title={isAI ? "Switch to rule-based insights" : "Enable AI-powered insights"}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all border ${
            isAI
              ? "bg-primary/15 border-primary/40 text-primary"
              : "bg-muted border-border text-muted-foreground hover:border-primary/30 hover:text-primary/70"
          } ${toggling ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <Sparkles className="w-3 h-3" strokeWidth={2} />
          {toggling ? "..." : isAI ? "AI ON" : "AI OFF"}
        </button>
      </div>

      {isAI && (
        <div className="flex items-center gap-1.5 mb-3 px-2.5 py-1.5 bg-primary/5 border border-primary/15 rounded-lg">
          <Sparkles className="w-3 h-3 text-primary" strokeWidth={1.5} />
          <span className="text-[10px] text-primary/80">Powered by AI · personalized for you</span>
        </div>
      )}

      <p className="text-sm text-foreground font-medium mb-3">{brief.greeting}</p>

      <div className="space-y-2 mb-4">
        {brief.insights?.map((insight, i) => {
          const Icon = ICON_MAP[insight.icon] || Target;
          return (
            <div key={i} className={`flex items-start gap-2.5 rounded-lg px-3 py-2 ${PRIORITY_STYLES[insight.priority] || ""}`}>
              <Icon className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              <span className="text-xs text-foreground">{insight.text}</span>
            </div>
          );
        })}
      </div>

      {brief.coach_tip && (
        <div className="border-t border-border pt-3">
          <p className="text-[11px] text-muted-foreground italic">"{brief.coach_tip}"</p>
        </div>
      )}
    </div>
  );
}

// ── Recent Commissions ─────────────────────────────────────────────────────────

const STATUS_BADGE = {
  pending:  "bg-amber-500/10 text-amber-400",
  approved: "bg-blue-500/10 text-blue-400",
  paid:     "bg-emerald-500/10 text-emerald-400",
  rejected: "bg-red-500/10 text-red-400",
};

function RecentCommissions({ items }) {
  if (!items?.length) return (
    <div className="text-center py-8 text-gray-600 text-sm">No earnings yet</div>
  );
  return (
    <div className="divide-y divide-border" data-testid="partner-recent-commissions">
      {items.map(c => (
        <div key={c.id} className="flex items-center justify-between py-3">
          <div>
            <div className="text-sm text-foreground">{c.source || "Referral"}</div>
            <div className="text-[11px] text-muted-foreground">{c.created_at ? fmtDate(c.created_at) : ""}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wide ${STATUS_BADGE[c.status] || "bg-gray-500/10 text-gray-400"}`}>
              {c.status}
            </span>
            <span className="text-sm font-medium text-foreground">₹{(c.amount || 0).toLocaleString("en-IN")}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [brief, setBrief] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);

  // Tour state
  const [tourStep, setTourStep] = useState(-1);
  const TOUR_KEY = "partner_tour_v1_done";

  useEffect(() => {
    const done = localStorage.getItem(TOUR_KEY);
    if (!done) {
      setTimeout(() => setTourStep(0), 800); // small delay for page render
    }
  }, []);

  const nextTourStep = () => {
    if (tourStep >= TOUR_STEPS.length - 1) {
      setTourStep(-1);
      localStorage.setItem(TOUR_KEY, "1");
    } else {
      setTourStep(tourStep + 1);
    }
  };

  const skipTour = () => {
    setTourStep(-1);
    localStorage.setItem(TOUR_KEY, "1");
  };

  const load = useCallback(async () => {
    try {
      const [statsRes, briefRes, commRes, kycRes] = await Promise.all([
        axios.get(`${API}/api/sm/partners/portal/stats`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/ai-brief`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/commissions?limit=5`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/kyc/status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setBrief(briefRes.data);
      setCommissions(commRes.data?.items || []);
      setKycStatus(kycRes.data?.kyc_status || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleAI = async () => {
    setToggling(true);
    try {
      await axios.post(`${API}/api/sm/partners/portal/ai-coach/toggle`, {}, { withCredentials: true });
      // Reload the brief (will now use AI or fall back based on new toggle state)
      const briefRes = await axios.get(`${API}/api/sm/partners/portal/ai-brief`, { withCredentials: true });
      setBrief(briefRes.data);
    } catch (e) {
      console.error("AI toggle failed", e);
    } finally {
      setToggling(false);
    }
  };

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "₹0";
  const firstName = (user?.name || "").split(" ")[0];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <>
      {/* Tour overlay */}
      {tourStep >= 0 && tourStep < TOUR_STEPS.length && (
        <TourOverlay
          step={TOUR_STEPS[tourStep]}
          total={TOUR_STEPS.length}
          onNext={nextTourStep}
          onSkip={skipTour}
        />
      )}

      <div className="space-y-8" data-testid="partner-dashboard">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-foreground tracking-tight">
              {greeting}, {firstName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {fmtDayLabel(new Date())}
            </p>
          </div>
          {stats?.tier && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
              style={{ borderColor: stats.tier.color + "40", backgroundColor: stats.tier.color + "10" }}>
              <Star className="w-3.5 h-3.5" style={{ color: stats.tier.color }} strokeWidth={1.5} />
              <span className="text-xs font-medium" style={{ color: stats.tier.color }}>{stats.tier.name} Partner</span>
            </div>
          )}
        </div>

        {/* KYC Alert Card (shown when not verified) */}
        {!loading && kycStatus && !["verified", "active", "approved"].includes(kycStatus) && (
          <KycStatusCard kycStatus={kycStatus} />
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="partner-kpi-cards">
          <KpiCard
            label="Total Referrals"
            value={loading ? "—" : (stats?.referral_count ?? 0)}
            sub={stats?.converted_count ? `${stats.converted_count} converted` : "No conversions yet"}
            icon={Users}
            color="#D4AF37"
            testId="kpi-referrals"
          />
          <KpiCard
            label="Pending Earnings"
            value={loading ? "—" : fmt(stats?.pending_commission)}
            sub="Awaiting approval"
            icon={IndianRupee}
            color="#60a5fa"
            testId="kpi-pending"
          />
          <KpiCard
            label="Total Earned"
            value={loading ? "—" : fmt(stats?.total_earned)}
            sub="Lifetime payouts"
            icon={TrendingUp}
            color="#34d399"
            testId="kpi-earned"
          />
        </div>

        {/* AI Brief + Commissions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AIBriefCard brief={brief} onToggleAI={handleToggleAI} toggling={toggling} />

          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] uppercase tracking-[0.15em] text-gray-500">Recent Earnings</span>
              <a href="/partner/commissions" className="text-[11px] text-primary hover:underline" data-testid="view-all-commissions">
                View all
              </a>
            </div>
            <RecentCommissions items={commissions} />
          </div>
        </div>

        {/* Quick tip to replay tour */}
        <div className="flex justify-center">
          <button
            onClick={() => { localStorage.removeItem(TOUR_KEY); setTourStep(0); }}
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors flex items-center gap-1"
            data-testid="replay-tour-btn"
          >
            <Sparkles className="w-3 h-3" /> Replay walkthrough
          </button>
        </div>
      </div>
    </>
  );
}
